import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { freigabenZahlNeu } from '@/components/layout/FreigabenZahl'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { frage } from '@/lib/dialoge'
import { feldKlasse } from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import { cancelApprovalRun, decideApproval, fetchApprovalRun, fetchApprovers, reassignApproval } from '../api/incoming-invoices.api'
import { AUFGABE_STATUS_TEXT, FREIGABEART_TEXT, MELDUNG_TEXT, RUECKFALL_TEXT, bereichText, tagText } from '../lib/texte'
import type { ApprovalRun, ApprovalTask, IncomingInvoiceDetail } from '../types/incoming-invoices.types'

const knopf = 'rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'

/** Der Freigabelauf der Rechnung — einmal geladen, für die Statusleiste und den Abschnitt „Freigabe". */
export function useFreigabelauf(rechnung: IncomingInvoiceDetail) {
  const [lauf, setLauf] = useState<ApprovalRun | null>(null)
  const hatLauf = rechnung.status !== 'UNCAPTURED' && rechnung.status !== 'DRAFT'
  useEffect(() => {
    if (!hatLauf) return setLauf(null)
    let aus = false
    fetchApprovalRun(rechnung.id)
      .then((l) => !aus && setLauf(l))
      .catch(() => !aus && setLauf(null))
    return () => {
      aus = true
    }
  }, [rechnung.id, rechnung.status, rechnung.updatedAt, hatLauf])
  return [lauf, setLauf] as const
}

/**
 * AP-34 11. — **Freigabe** an der Rechnung: wer freigeben muss und warum, die eigene Aufgabe mit
 * „Freigeben" und „Ablehnen", für die Freigabeverwaltung „Neu zuweisen" und „Lauf abbrechen".
 *
 * Entscheiden ist eine Handlung und hat deshalb Knöpfe mit dem Verb (`speichern.md`, Fall 3).
 */
export function Freigabe({
  rechnung,
  lauf,
  setLauf,
  onRechnung,
  onFehler,
}: {
  rechnung: IncomingInvoiceDetail
  lauf: ApprovalRun | null
  setLauf: Dispatch<SetStateAction<ApprovalRun | null>>
  onRechnung: (r: IncomingInvoiceDetail) => void
  onFehler: (t: string | null) => void
}) {
  const terms = useTerminology()
  const { user } = useAuthStore()
  const [kommentar, setKommentar] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [freigeber, setFreigeber] = useState<{ id: string; name: string }[] | null>(null)
  const verwalten = erfuelltRecht('incoming_invoices.approval.manage', user?.permissions)

  useEffect(() => {
    if (!verwalten || rechnung.status !== 'IN_APPROVAL') return
    let aus = false
    fetchApprovers()
      .then((f) => !aus && setFreigeber(f))
      .catch(() => !aus && setFreigeber([]))
    return () => {
      aus = true
    }
  }, [verwalten, rechnung.status])

  const tue = async (was: () => Promise<IncomingInvoiceDetail | void>, fehlertext: string) => {
    try {
      setLaeuft(true)
      onFehler(null)
      const r = await was()
      if (r) onRechnung(r)
      void freigabenZahlNeu()
    } catch (e) {
      onFehler(apiErrorText(e, fehlertext))
    } finally {
      setLaeuft(false)
    }
  }

  if (!lauf || (!lauf.tasks.length && !lauf.releaseKind)) return null
  const meine = lauf.tasks.find((a) => a.mine)
  const entscheide = (a: ApprovalTask, entscheidung: 'APPROVE' | 'REJECT') =>
    tue(async () => {
      const r = await decideApproval(a.id, entscheidung, kommentar.trim() || null)
      setKommentar('')
      return r
    }, 'Die Entscheidung ließ sich nicht speichern.')
  const abbrechen = async () => {
    const grund = await frage('Freigabelauf abbrechen? Die offenen Aufgaben werden zurückgezogen, die Rechnung geht zurück in die Erfassung. Grund:', '', { pflicht: true, ok: 'Lauf abbrechen' })
    if (!grund) return
    await tue(() => cancelApprovalRun(rechnung.id, grund), 'Der Lauf ließ sich nicht abbrechen.')
  }
  const zuweisen = (a: ApprovalTask, userId: string) =>
    tue(async () => {
      setLauf(await reassignApproval(a.id, userId))
    }, 'Die Aufgabe ließ sich nicht zuweisen.')

  return (
    <div className="space-y-3 text-sm">
      {lauf.ruleName && (
        <p className="text-muted-foreground">
          Regel „{lauf.ruleName}"{lauf.reasons.length > 0 && <> — Anlass: {lauf.reasons.map((a) => MELDUNG_TEXT[a] ?? a).join(', ')}</>}
        </p>
      )}
      {rechnung.status === 'RELEASED' || rechnung.status === 'EXPORTED' ? (
        <p>
          {FREIGABEART_TEXT[lauf.releaseKind ?? ''] ?? 'freigegeben'}
          {lauf.releasedAt && <> am {tagText(lauf.releasedAt)}</>}
          {lauf.releasedByName && <> durch {lauf.releasedByName}</>}
          {!lauf.ruleName && lauf.reasons.length > 0 && <span className="text-muted-foreground"> — Anlässe: {lauf.reasons.map((a) => MELDUNG_TEXT[a] ?? a).join(', ')}</span>}
        </p>
      ) : null}

      {lauf.tasks.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {lauf.tasks.map((a) => {
            const s = AUFGABE_STATUS_TEXT[a.status]
            return (
              <li key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                {lauf.stageCount > 1 && <span className="text-xs text-muted-foreground">Stufe {a.stageNo}</span>}
                <span className="font-medium">{a.approverName}</span>
                <span className="text-xs text-muted-foreground">{bereichText(a.scopeKind, terms.project)}</span>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.klasse}`}>{s.label}</span>
                {a.decidedAt && <span className="text-xs text-muted-foreground">{tagText(a.decidedAt)}</span>}
                {a.fallbackReason && <span className="text-xs text-muted-foreground">Rückfall-Freigeber: {RUECKFALL_TEXT[a.fallbackReason] ?? a.fallbackReason}</span>}
                {a.reassignedFromName && <span className="text-xs text-muted-foreground">neu zugewiesen, vorher bei {a.reassignedFromName}</span>}
                {a.comment && <span className="basis-full text-sm">„{a.comment}"</span>}
                {verwalten && a.status === 'OPEN' && freigeber && (
                  <select aria-label={`Aufgabe von ${a.approverName} neu zuweisen`} className={`${feldKlasse} ml-auto w-48`} value="" disabled={laeuft} onChange={(e) => e.target.value && void zuweisen(a, e.target.value)}>
                    <option value="">Neu zuweisen …</option>
                    {freigeber
                      // nicht an den bisherigen Freigeber und nicht an den Sachbearbeiter — niemand gibt seine eigene Erfassung frei
                      .filter((f) => f.id !== a.approverUserId && f.id !== rechnung.clerkUserId)
                      .map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                  </select>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {meine && (
        <div className="rounded-md border-2 border-amber-500/70 bg-amber-500/10 px-3 py-3">
          <p className="text-base font-semibold">Diese Rechnung wartet auf deine Freigabe.</p>
          <textarea aria-label="Kommentar" className={`${feldKlasse} mt-2 min-h-[4rem]`} placeholder="Kommentar — zum Ablehnen Pflicht" value={kommentar} onChange={(e) => setKommentar(e.target.value)} />
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50" disabled={laeuft} onClick={() => void entscheide(meine, 'APPROVE')}>
              Freigeben
            </button>
            <button
              type="button"
              className="rounded-md border border-destructive/50 px-4 py-1.5 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
              disabled={laeuft || !kommentar.trim()}
              title={kommentar.trim() ? undefined : 'Zum Ablehnen braucht es eine Begründung'}
              onClick={() => void entscheide(meine, 'REJECT')}
            >
              Ablehnen
            </button>
          </div>
        </div>
      )}

      {verwalten && rechnung.status === 'IN_APPROVAL' && (
        <button type="button" className={knopf} disabled={laeuft} onClick={() => void abbrechen()}>
          Lauf abbrechen
        </button>
      )}
    </div>
  )
}
